import React, { useState } from 'react';
import { makeStyles, Theme } from '@material-ui/core';
import { colors } from '../../../theme/tokens';

const CORAL = colors.brandStrong;

const useStyles = makeStyles((theme: Theme) => ({
  wrap: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minWidth: 0,
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  title: {
    fontSize: 11,
    fontWeight: 700,
    color: colors.textFaint,
    textTransform: 'uppercase' as any,
    letterSpacing: '0.06em',
  },
  pageIndicator: { fontSize: 10, fontWeight: 600, color: colors.textFaint },
  card: {
    background: colors.bgSurface,
    borderRadius: 12,
    border: `1px solid ${colors.border}`,
    boxShadow: `0 1px 3px ${colors.shadow}`,
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    boxSizing: 'border-box' as any,
  },
  row: {
    padding: '10px 16px',
    borderBottom: `1px solid ${colors.border}`,
    '&:last-child': { borderBottom: 'none' },
  },
  emptyText: {
    fontSize: 13,
    color: colors.textDisabled,
    textAlign: 'center' as any,
    padding: '24px 16px',
  },
  pagerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 10px',
    borderTop: `1px solid ${colors.border}`,
  },
  pagerBtn: {
    fontSize: 11,
    fontWeight: 700,
    color: CORAL,
    background: colors.brandTintSoft,
    border: 'none',
    borderRadius: 6,
    padding: '6px 12px',
    cursor: 'pointer',
    '&:disabled': {
      color: colors.textDisabled,
      background: 'transparent',
      cursor: 'default',
    },
  },
}));

export interface PaginatedListCardProps<T> {
  title: string;
  items: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  pageSize?: number;
  emptyText?: string;
}

export default function PaginatedListCard<T>({
  title,
  items,
  renderItem,
  pageSize = 8,
  emptyText = 'Nothing to show yet',
}: PaginatedListCardProps<T>) {
  const classes = useStyles();
  const [page, setPage] = useState(0);
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const clampedPage = Math.min(page, totalPages - 1);
  const start = clampedPage * pageSize;
  const visible = items.slice(start, start + pageSize);

  return (
    <div className={classes.wrap}>
      <div className={classes.headerRow}>
        <div className={classes.title}>{title}</div>
        {items.length > 0 && (
          <span className={classes.pageIndicator}>
            {clampedPage + 1} / {totalPages}
          </span>
        )}
      </div>
      <div
        className={classes.card}
        style={{ display: 'flex', flexDirection: 'column' }}
      >
        <div style={{ flex: 1, overflow: 'auto' }}>
          {items.length === 0 ? (
            <div className={classes.emptyText}>{emptyText}</div>
          ) : (
            visible.map((item, i) => (
              <div key={start + i} className={classes.row}>
                {renderItem(item, start + i)}
              </div>
            ))
          )}
        </div>
        {items.length > pageSize && (
          <div className={classes.pagerRow}>
            <button
              className={classes.pagerBtn}
              disabled={clampedPage === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              ← Previous
            </button>
            <button
              className={classes.pagerBtn}
              disabled={clampedPage >= totalPages - 1}
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
